import React, { useEffect, useState } from "react";
import {
  Td,
  Text,
  Textarea,
  HStack,
  VStack,
  IconButton,
  useToast,
  useMediaQuery,
  useColorModeValue,
  Icon
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaRegCopy,
  FaCheck,
  FaStopwatch,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

function extractOTP(message) {
  const match = message.match(/\b\d{4,6}\b/);
  return match ? match[0] : null;
}
// Helper functions
function getRemainingTimeInSeconds(backendTimeISO, serverName) {
  const backendTime = new Date(backendTimeISO);
  const currentTime = new Date();
  const diffMs = currentTime - backendTime;
  const minutes =
    serverName === "server3" ? 5 : serverName === "server4" ? 15 : 20;
  const maxMs = minutes * 60 * 1000;
  return diffMs < maxMs ? Math.floor((maxMs - diffMs) / 1000) : 0;
}
function getServerName(server) {
  if (server == 'server1') {
    return 'server1 online'
  } else if (server == 'server2') {
    return 'server2 otpmarket'
  }else{
    return 'server4 phantom'
  }
}

const formatTime = (time) => {
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
};

const getStatusIcon = (status) => {
  switch (status) {
    case "success":
      return <FaCheckCircle size={18} color="#319795" />;
    case "cancelled":
      return <FaTimesCircle size={18} color="#E53E3E" />;
    case "pending":
      return <FaClock size={18} color="#D69E2E" />;
    default:
      return <FaClock size={18} color="#718096" />;
  }
};


const AdminOtpHistoryCard = ({
  otp,
  index,
  themeColor,
  borderColor,
  textColor,
  subTextColor
}) => {
  const toast = useToast();
  const navigate = useNavigate();

  const [remainingTime, setRemainingTime] = useState(
    getRemainingTimeInSeconds(otp.createdAt, otp?.service?.server)
  );
  const [showTime, setShowTime] = useState(false);
  const [copied, setCopied] = useState("");
  const [isLargerThan650] = useMediaQuery("(min-width: 650px)");
  const textareaBg = useColorModeValue("gray.50", "gray.700");
  const textareaBorder = useColorModeValue("gray.200", "gray.600");

  useEffect(() => {
    const initial = getRemainingTimeInSeconds(
      otp.createdAt,
      otp?.service?.server
    );
    if (initial > 0 && otp.status === "pending") {
      setShowTime(true);
    }
  }, [otp]);

  useEffect(() => {
    if (showTime && remainingTime > 0 && otp.status === "pending") {
      const interval = setInterval(() => {
        setRemainingTime((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [showTime, remainingTime, otp.status]);

  const copyToClipboard = (text, type = "Number") => {
    let value = type === "OTP" ? extractOTP(text) : text?.slice(3);
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(type);
        toast({
          title: `${value} ${type === "Number" ? "number" : "OTP"} copied`,
          status: "info",
          duration: 800,
          position: "bottom-right",
        });
        setTimeout(() => setCopied(""), 2000);
      })
      .catch(() =>
        toast({
          title: "Failed to copy!",
          status: "error",
          duration: 1000,
        })
      );
  };

  return (
    <motion.tr
      key={otp._id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      style={{ textAlign: "center" }}
    >
      {/* USER / BALANCE / TIMER */}
      <Td borderBottom="1px solid" borderColor={borderColor} py="0.8rem">
        <VStack spacing={1} align="center">
          {otp.status === "pending" && showTime && (
            <HStack spacing={1}>
              <FaStopwatch size={14} color="#718096" />
              <Text fontSize="sm" fontWeight="700" color="red.500">
                {formatTime(remainingTime)}
              </Text>
            </HStack>
          )}

          <HStack spacing={2}>
            <Text fontSize="xs" color={subTextColor}>
              Aval:{" "}
              <Text as="span" color="green.500" fontWeight="semibold">
                ₹{Number(otp.user.balance).toFixed(1)}
              </Text>
            </Text>
            <Text fontSize="xs" color={subTextColor}>
              Total:{" "}
              <Text as="span" color="yellow.500" fontWeight="semibold">
                ₹{Number(otp.user.walletBalance).toFixed(1)}
              </Text>
            </Text>
          </HStack>

          <Text
            fontSize="xs"
            color={subTextColor}
            cursor="pointer"
            _hover={{ textDecoration: "underline", color: themeColor }}
            onClick={() => navigate(`/admin/otp-history?userId=${otp.user._id}`)}
          >
            {otp.user.email}
          </Text>
        </VStack>
      </Td>

      {/* SERVICE */}
      <Td borderBottom="1px solid" borderColor={borderColor}>
        <VStack spacing={0.5}>
          <Text fontSize="sm" fontWeight="medium" color={textColor}>
            {otp?.service?.serviceName}
          </Text>
          <Text fontSize="xs" color={subTextColor}>
            Server:  {getServerName(otp.service?.server)}
          </Text>
        </VStack>
      </Td>

      {/* PRICE */}
      <Td borderBottom="1px solid" borderColor={borderColor}>
        <Text fontWeight="medium" color={textColor}>₹{otp.price.toFixed(2)}</Text>
      </Td>

      {/* MOBILE */}
      <Td borderBottom="1px solid" borderColor={borderColor}>
        <HStack justify="center" spacing={1}>
          <Text fontFamily="mono" fontSize="sm" color={textColor}>
            {otp.mobileno}
          </Text>
          <IconButton
            size="xs"
            variant="ghost"
            icon={
              copied === "Number" ? (
                <FaCheck size={13} color="green" />
              ) : (
                <FaRegCopy size={13} />
              )
            }
            onClick={() => copyToClipboard(otp.mobileno, "Number")}
            aria-label="Copy number"
            color={subTextColor}
          />
        </HStack>
      </Td>

      {/* STATUS */}
      <Td borderBottom="1px solid" borderColor={borderColor}>
        <HStack justify="center">{getStatusIcon(otp.status)}</HStack>
      </Td>

      {/* OTP */}
      <Td borderBottom="1px solid" borderColor={borderColor}>
        {otp.otpIds?.length ? (
          <VStack position="relative">
            <Textarea
              value={otp.otpIds.map((el) => extractOTP(el.otpMessage)).join(" | ")}
              readOnly
              fontSize="xs"
              fontFamily="mono"
              resize="none"
              bg={textareaBg}
              borderColor={textareaBorder}
              color={textColor}
              _focus={{ borderColor: themeColor }}
            />
            <IconButton
              size="xs"
              position="absolute"
              top="5px"
              right="5px"
              aria-label="Copy OTP"
              icon={
                copied === "OTP" ? (
                  <FaCheck size={13} color="green" />
                ) : (
                  <FaRegCopy size={13} />
                )
              }
              onClick={() =>
                copyToClipboard(
                  otp.otpIds[otp.otpIds.length - 1].otpMessage,
                  "OTP"
                )
              }
              variant="ghost"
              color={subTextColor}
            />
          </VStack>
        ) : (
          <Textarea
            readOnly
            value={otp.status === "pending" ? "Waiting for OTP..." : ""}
            fontSize="xs"
            bg={textareaBg}
            borderColor={textareaBorder}
            color={subTextColor}
            _focus={{ borderColor: themeColor }}
          />
        )}
      </Td>

      {/* DATE */}
      <Td borderBottom="1px solid" borderColor={borderColor}>
        {isLargerThan650 ? (
          <Text color={textColor} fontSize="sm">{otp.time}</Text>
        ) : (
          <Text color={textColor} fontSize="sm">
            {otp.time.split(" : ")[0]}
            <br />
            {otp.time.split(" : ")[1]}
          </Text>
        )}
      </Td>
    </motion.tr>
  );
};

export default AdminOtpHistoryCard;
